import { Minus, ShieldAlert, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useImpersonationStatus } from '@/hooks/use-impersonation-status'
import { useStopImpersonating } from '@/features/super-admin/hooks/use-stop-impersonating'
import { useTranslation } from '@/hooks/use-translation'

interface ImpersonationBannerProps {
  minimized: boolean
  onMinimize: () => void
  onExpand: () => void
}

export function ImpersonationBanner({ minimized, onMinimize, onExpand }: ImpersonationBannerProps) {
  const { isImpersonating, currentUser } = useImpersonationStatus()
  const { stopImpersonating, isStopping } = useStopImpersonating()
  const t = useTranslation()

  if (!isImpersonating) return null

  const userName = currentUser?.name || 'Unknown User'
  const userEmail = currentUser?.email || ''
  const bannerText = t('superAdmin.impersonation.bannerText', { name: userName, email: userEmail })

  if (minimized) {
    return (
      <button
        type="button"
        onClick={onExpand}
        aria-label={t('superAdmin.impersonation.expand')}
        title={bannerText}
        className="fixed top-2 right-2 z-50 flex h-9 w-9 items-center justify-center rounded-full bg-warning-500 text-white shadow-lg ring-2 ring-white transition-transform hover:scale-105 hover:bg-warning-600 animate-scale-in"
      >
        <ShieldAlert className="h-5 w-5" />
      </button>
    )
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-12 bg-warning-500 text-white shadow-lg flex items-center gap-3 px-4 animate-slide-down">
      <ShieldAlert className="h-5 w-5 shrink-0" />
      <span className="min-w-0 truncate text-sm font-medium">{bannerText}</span>
      <div className="ml-auto flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => stopImpersonating()}
          disabled={isStopping}
        >
          <X className="h-4 w-4 mr-1" />
          {t('superAdmin.impersonation.stopImpersonating')}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onMinimize}
          aria-label={t('superAdmin.impersonation.minimize')}
          className="h-8 w-8 text-white hover:bg-warning-600 hover:text-white"
        >
          <Minus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
